import { ApiError, apiRequest } from "./client.js";
import { fetchPage, type Page } from "./collection.js";
import { parseTrack, parseTracks } from "./parse.js";
import type { Track } from "./types.js";

/**
 * The library: one pool, owned by no feed.
 *
 * Reading it is a page at a time. Adding to it is three steps, and the shape of them
 * is the API's, not this app's (`apps/api/src/media.ts`):
 *
 *   1. `POST /api/media/uploads` mints a private, expiring ticket to put the bytes
 *      somewhere the API can reach them. Nothing is in the library yet.
 *   2. the browser PUTs the file straight to that address. It never passes through
 *      the API, and the request carries no credential of ours — the signature is the
 *      credential, and attaching the operator's bearer to a third party would be a
 *      leak with no purpose.
 *   3. `POST /api/tracks` names the staged object **and the checksum the browser
 *      computed itself**. The API re-downloads what was staged, re-hashes it, decodes
 *      it and refuses if any of the three disagree. So the hash is computed here, over
 *      the exact bytes that were sent, before they are sent — it is the browser's half
 *      of a proof, not a formality.
 *
 * A 200 rather than a 201 from step 3 means the library already held this audio: the
 * pool deduplicates by content, globally, and the existing track is the answer.
 */

const TRACKS_PATH = "/api/tracks";

export interface TrackPageRequest {
  cursor?: string | null;
  limit?: number;
  search?: string;
  signal?: AbortSignal | undefined;
}

export async function fetchTrackPage(
  request: TrackPageRequest = {},
): Promise<Page<Track>> {
  return fetchPage(TRACKS_PATH, parseTracks, {
    cursor: request.cursor ?? null,
    ...(request.limit === undefined ? {} : { limit: request.limit }),
    ...(request.search === undefined || request.search.trim() === ""
      ? {}
      : { q: request.search.trim() }),
    signal: request.signal,
  });
}

const trackPath = (trackId: string, suffix = "") =>
  `${TRACKS_PATH}/${encodeURIComponent(trackId)}${suffix}`;

export interface TrackPatchInput {
  title?: string;
  artist?: string;
  album?: string;
  year?: number | null;
  genre?: string;
  tags?: string[];
  cue_in_ms?: number;
  cue_out_ms?: number | null;
}

export async function patchTrack(
  trackId: string,
  input: TrackPatchInput,
): Promise<Track | null> {
  return parseTrack(
    await apiRequest<unknown>(trackPath(trackId), {
      method: "PATCH",
      body: input,
    }),
  );
}

export async function deleteTrack(trackId: string): Promise<void> {
  await apiRequest<void>(trackPath(trackId), { method: "DELETE" });
}

interface UploadTicket {
  key: string;
  url: string;
  requiredHeaders: Record<string, string>;
}

interface TicketRequest {
  kind: "audio" | "artwork";
  fileName: string;
  contentType: string;
  byteSize: number;
}

async function requestTicket(request: TicketRequest): Promise<UploadTicket> {
  const body = await apiRequest<{
    key?: unknown;
    url?: unknown;
    required_headers?: unknown;
  }>("/api/media/uploads", {
    method: "POST",
    body: {
      kind: request.kind,
      file_name: request.fileName,
      content_type: request.contentType,
      byte_size: request.byteSize,
    },
  });
  if (typeof body.key !== "string" || typeof body.url !== "string") {
    throw new ApiError(0, "the upload ticket was not usable");
  }
  const headers: Record<string, string> = {};
  const declared = body.required_headers;
  if (typeof declared === "object" && declared !== null) {
    for (const [name, value] of Object.entries(declared)) {
      // Content-length is the browser's to set from the body, and it refuses to
      // let a script set it. Every other header the ticket names is sent as named.
      if (name.toLowerCase() === "content-length") continue;
      headers[name] = String(value);
    }
  }
  return { key: body.key, url: body.url, requiredHeaders: headers };
}

/** The checksum the API will re-compute and compare against. */
export async function sha256Hex(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    await file.arrayBuffer(),
  );
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

async function putToStaging(
  ticket: UploadTicket,
  file: Blob,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(ticket.url, {
    method: "PUT",
    headers: ticket.requiredHeaders,
    body: file,
    signal: signal ?? null,
  }).catch(() => null);
  if (!response) {
    throw new ApiError(0, "the media store could not be reached");
  }
  if (!response.ok) {
    throw new ApiError(
      response.status,
      `the media store refused the upload (HTTP ${String(response.status)})`,
    );
  }
}

/** What the operator can say about a track at the moment he adds it. */
export interface TrackDraft {
  title: string;
  artist: string;
  album: string;
  genre: string;
  year: number | null;
}

export function draftFromFileName(fileName: string): TrackDraft {
  const stem = fileName.replace(/\.[^./\\]+$/, "").trim();
  // "Artist - Title" is the convention nearly every exported library uses, so it is
  // read when it is there and never invented when it is not.
  const dash = stem.indexOf(" - ");
  const artist = dash > 0 ? stem.slice(0, dash).trim() : "";
  const title = dash > 0 ? stem.slice(dash + 3).trim() : stem;
  return {
    title: title === "" ? fileName : title,
    artist,
    album: "",
    genre: "",
    year: null,
  };
}

/**
 * The three steps, named, so a queue of files can say which one each is in. They are
 * the API's steps rather than a progress bar's invention: hashing is the browser's
 * half of the proof, uploading is the bytes leaving, and validating is the API
 * re-hashing and decoding what it received.
 */
export type UploadStage = "hashing" | "uploading" | "validating";

export async function addTrack(
  file: File,
  draft: TrackDraft,
  onStage: (stage: UploadStage) => void = () => undefined,
  signal?: AbortSignal,
): Promise<Track | null> {
  onStage("hashing");
  const sha256 = await sha256Hex(file);
  const ticket = await requestTicket({
    kind: "audio",
    fileName: file.name,
    contentType: file.type || "application/octet-stream",
    byteSize: file.size,
  });
  onStage("uploading");
  await putToStaging(ticket, file, signal);
  onStage("validating");
  return parseTrack(
    await apiRequest<unknown>(TRACKS_PATH, {
      method: "POST",
      body: {
        r2_key: ticket.key,
        sha256,
        title: draft.title,
        artist: draft.artist,
        album: draft.album,
        genre: draft.genre,
        year: draft.year,
      },
      signal,
    }),
  );
}

/**
 * Artwork is a property of the track, so every feed playing it shows the same one.
 * The new media id comes back, which is also what tells the browser that the bytes
 * behind `/api/artwork/{track}` are not the ones it already has.
 */
export async function attachArtwork(
  trackId: string,
  file: File,
  signal?: AbortSignal,
): Promise<string | null> {
  const sha256 = await sha256Hex(file);
  const ticket = await requestTicket({
    kind: "artwork",
    fileName: file.name,
    contentType: file.type || "application/octet-stream",
    byteSize: file.size,
  });
  await putToStaging(ticket, file, signal);
  const body = await apiRequest<{ artwork_media_id?: unknown }>(
    trackPath(trackId, "/artwork"),
    {
      method: "PUT",
      body: { r2_key: ticket.key, sha256 },
      signal,
    },
  );
  return typeof body.artwork_media_id === "string"
    ? body.artwork_media_id
    : null;
}
